import React, { useState, useEffect } from 'react';
import { metadataAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PasswordStrengthChecker, { meetsPolicy } from '../components/PasswordStrengthChecker';
import toast from 'react-hot-toast';
import { Plus, Users, Eye, EyeOff } from 'lucide-react';

export default function ClientUsersPage() {
  const { isAdmin, isSupportLead } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // New user form
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    metadataAPI.getClients().then(r => setClients(r.data)).catch(() => {});
  }, []);

  const loadUsers = async (clientId) => {
    if (!clientId) { setUsers([]); return; }
    setLoading(true);
    try {
      const resp = await metadataAPI.getClientUsers(clientId);
      setUsers(resp.data);
    } catch (err) {
      // Client users endpoint requires support_lead_or_admin
      toast.error(err.response?.data?.detail || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (id) => {
    setSelectedClient(id);
    loadUsers(id);
    setShowForm(false);
    resetForm();
  };

  const resetForm = () => {
    setNewEmail('');
    setNewName('');
    setNewPassword('');
    setShowPassword(false);
  };

  const canSubmit = newEmail && newName && meetsPolicy(newPassword) && !creating;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    try {
      await authAPI.createClientUser({
        email: newEmail,
        name: newName,
        password: newPassword,
        client_id: parseInt(selectedClient),
      });
      toast.success(`User ${newName} created`);
      resetForm();
      setShowForm(false);
      loadUsers(selectedClient);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join('; '));
      } else {
        toast.error('Failed to create user');
      }
    } finally {
      setCreating(false);
    }
  };

  if (!isAdmin && !isSupportLead) {
    return (
      <div>
        <div className="page-header"><h1>Client users</h1></div>
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          Access restricted to Support Lead and Admin.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Client users</h1>
        <p>Create and manage client user accounts for the support portal</p>
      </div>

      <div className="card">
        {/* Client selector */}
        <div style={{ padding: '0 16px 16px', display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: 400 }}>
            <label className="form-label">Client</label>
            <select className="form-select" value={selectedClient} onChange={e => handleClientChange(e.target.value)}>
              <option value="">Select client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>
              ))}
            </select>
          </div>
          {selectedClient && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
              <Plus size={14} /> {showForm ? 'Cancel' : 'New user'}
            </button>
          )}
        </div>

        {/* Create user form */}
        {showForm && selectedClient && (
          <div style={{ margin: '0 16px 16px', padding: 16, background: 'var(--color-bg-subtle, #f9fafb)', borderRadius: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Create client user</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="form-label">Full name <span className="required">*</span></label>
                <input className="form-input" value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Maria Santos" />
              </div>
              <div>
                <label className="form-label">Email <span className="required">*</span></label>
                <input type="email" className="form-input" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  placeholder="e.g. maria.santos@company.com" />
              </div>
            </div>

            <div style={{ maxWidth: 400, marginBottom: 12 }}>
              <label className="form-label">Password <span className="required">*</span></label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Strong password required"
                  style={{ paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}
                  tabIndex={-1}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <PasswordStrengthChecker password={newPassword} />
            </div>

            <button className="btn btn-primary" onClick={handleCreate} disabled={!canSubmit}>
              {creating ? 'Creating...' : 'Create user'}
            </button>
          </div>
        )}

        {/* User list */}
        {selectedClient && !loading && (
          <div style={{ padding: '0 16px 16px' }}>
            {users.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                <Users size={20} style={{ marginBottom: 4, display: 'block', margin: '0 auto 8px' }} />
                No users for this client. Click "New user" to create one.
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>{u.name}</td>
                      <td style={{ padding: '8px', color: 'var(--color-text-muted)' }}>{u.email}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ fontSize: 11, color: '#059669' }}>Active</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {selectedClient && loading && (
          <p style={{ padding: '16px', color: 'var(--color-text-muted)', fontSize: 13 }}>Loading users...</p>
        )}
      </div>
    </div>
  );
}

const thStyle = { textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)' };
